import { createSelector } from 'reselect';
import get from 'lodash/get';
import { set, keyBy, flatten, uniq } from 'lodash';
import { initialState } from './reducer';
import { createSelectFieldOptions, updateMediaStructure } from '../../utils/common';
import { groupRepertoireRecords } from './adapters';

const selectArtistDomain = state => state.Artist || initialState;

const selectChosenProfileData = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'profileData.entity'));

const selectRepetorieModalStatus = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'repertoireModal'));

const selectChosenRepetorie = () => createSelector(selectArtistDomain, subState => get(subState, 'chosenRepetorie'));

const selectChosenRepetorieProductions = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'repetorieProductions'));

const selectRepetorieImages = () => createSelector(selectArtistDomain, subState => get(subState, 'repetorieImages'));
const selectRepetorieVideos = () => createSelector(selectArtistDomain, subState => get(subState, 'repetorieVideos'));

const selectChosenProfileAgreements = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'artistAgreements'));

const selectChosenProfileReviews = () => createSelector(selectArtistDomain, subState => get(subState, 'reviews'));

const selectArtistPerformedDates = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'artistDatesInProductions.data', []));

const selectArtistProductionsPrivacy = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'productionsPrivacyAttributes.data', []));

const selectChosenProfileProductions = () =>
  createSelector(
    selectArtistPerformedDates(),
    selectArtistProductionsPrivacy(),
    selectArtistDomain,
    (dates, privacyAttributes, subState) => {
      const selectedDates = keyBy(dates, 'productionId');
      const productions = get(subState, 'artistProductions.data', []);
      const privacyAttr = keyBy(privacyAttributes, 'production.id');
      productions.forEach(p => {
        const allSelectedDates = uniq(flatten(Object.values(get(selectedDates, `${p?.id}.artistDates`, []))));
        set(
          p,
          'dates',
          get(p, 'dates', []).map(day => ({ ...day, performed: allSelectedDates.includes(day?.date) })),
        );
        set(p, 'privacyAttribute', get(privacyAttr, p?.id, {}));
      });
      return { ...get(subState, 'artistProductions', []), data: productions };
    },
  );

const selectSearchProductionsList = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'searchProductions'));

const selectProductionsCasts = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'productionCasts.data', []));

const selectChosenProfileRepertoire = () => createSelector(selectArtistDomain, subState => get(subState, 'repertoire'));

const selectChosenProfileDisplayRepertoire = () =>
  createSelector(selectArtistDomain, subState => {
    const repertoire = get(subState, 'repertoire.data');
    const future = repertoire?.filter(item => item?.status === 'future');
    const retired = repertoire?.filter(item => item?.status === 'retire');
    const active = repertoire?.filter(item => item?.status !== 'retire' && item?.status !== 'future');
    return {
      ...get(subState, 'repertoire'),
      all: groupRepertoireRecords(repertoire),
      future: groupRepertoireRecords(future),
      retired: groupRepertoireRecords(retired),
      active: groupRepertoireRecords(active),
      short: {
        all: groupRepertoireRecords(repertoire?.slice(0, 4)),
        future: groupRepertoireRecords(future?.slice(0, 4)),
      },
    };
  });

const selectChosenProfileBio = () => createSelector(selectArtistDomain, subState => get(subState, 'bio'));

const selectProfileContacts = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'profileContacts.data', []));

const selectChosenProfileSeasons = () =>
  createSelector(selectArtistDomain, subState =>
    createSelectFieldOptions(get(subState, 'seasons.data', []), 'id', 'name'),
  );

const selectChosenProfileWorks = () =>
  createSelector(selectArtistDomain, subState => ({
    ...get(subState, 'worksList'),
    options: createSelectFieldOptions(get(subState, 'worksList.data', []), 'id', 'name'),
    startSearching: get(subState, 'worksListStartSearching', false),
  }));
const selectLangs = () => createSelector(selectArtistDomain, subState => get(subState, 'langs.data', []));

const selectWishlistRemainingInfo = () =>
  createSelector(selectArtistDomain, subState => ({
    added: get(subState, `${initialState.wishlist.listName}.total`),
    total: 5,
  }));

const getRoleQualification = role => (role.professionQualification ? `(${role.professionQualification.name})` : '');

const selectArtistProfessionsOptions = () =>
  createSelector(selectArtistDomain, subState => {
    const professions = get(subState, 'professions.data', []).filter(prof => prof.slug !== 'singer');
    return createSelectFieldOptions(professions, 'id', 'name', { type: 'profession' }, true);
  });

const selectArtistRolesOptions = () =>
  createSelector(selectArtistDomain, subState => {
    const roles = get(subState, 'roles.data', []).map(role => ({
      ...role,
      ...{ name: `${role.name} ${getRoleQualification(role)}` },
    }));
    return createSelectFieldOptions(roles, 'id', 'name', { type: 'role' }, true);
  });

const selectLastUpdatedRepertoireEntry = () =>
  createSelector(
    selectArtistDomain,
    subState =>
      [...get(subState, 'repertoire.data', [])].sort((a, b) => new Date(a.updatedAt) < new Date(b.updatedAt))[0],
  );

const selectImages = () =>
  createSelector(selectArtistDomain, subState => {
    const images = get(subState, 'images.data', []);
    return {
      ...get(subState, 'images', {}),
      data: updateMediaStructure(images),
    };
  });

const selectBannerImages = () =>
  createSelector(selectArtistDomain, subState => {
    const images = get(subState, 'bannerImages.data', []);
    return {
      ...get(subState, 'bannerImages', {}),
      data: updateMediaStructure(images),
    };
  });

const selectVideo = () =>
  createSelector(selectArtistDomain, subState => {
    const videos = get(subState, 'video.data', []);
    return {
      ...get(subState, 'video', {}),
      data: updateMediaStructure(videos),
    };
  });

const selectMediaAttrTypesOptions = () =>
  createSelector(selectArtistDomain, subState =>
    createSelectFieldOptions(get(subState, 'attributeTypes.data', []), 'id', 'name'),
  );

const selectLoading = () => createSelector(selectArtistDomain, subState => get(subState, 'profileData.loading', false));

const selectUpdateMediaLoading = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'mediaUpdating', false));

const selectBannerLoading = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'bannerImages.loading', false));

const selectArtistProductionLoading = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'artistProductions.privacyLoading', null));

const selectArtistProductions = () => createSelector(selectArtistDomain, subState => get(subState, 'productions', {}));

const selectArtistProductionsLoading = () =>
  createSelector(selectArtistDomain, subState => get(subState, 'productions.loading', false));

const selectArtistVideoFormatsNames = () =>
  createSelector(selectArtistDomain, subState =>
    get(subState, `${initialState.videoFormats.listName}.data`, [])?.map(format => format?.slug),
  );

const selectArtistImageFormatsNames = () =>
  createSelector(selectArtistDomain, subState =>
    get(subState, `${initialState.imageFormats.listName}.data`, [])?.map(format => format?.slug),
  );

export {
  selectChosenProfileData,
  selectChosenProfileBio,
  selectChosenProfileRepertoire,
  selectChosenProfileReviews,
  selectChosenProfileProductions,
  selectLangs,
  selectProfileContacts,
  selectChosenProfileAgreements,
  selectChosenProfileSeasons,
  selectChosenProfileWorks,
  selectSearchProductionsList,
  selectArtistProfessionsOptions,
  selectArtistRolesOptions,
  selectProductionsCasts,
  selectWishlistRemainingInfo,
  selectLastUpdatedRepertoireEntry,
  selectImages,
  selectBannerImages,
  selectMediaAttrTypesOptions,
  selectVideo,
  selectLoading,
  selectRepetorieModalStatus,
  selectRepetorieImages,
  selectRepetorieVideos,
  selectChosenRepetorieProductions,
  selectChosenRepetorie,
  selectUpdateMediaLoading,
  selectBannerLoading,
  selectArtistProductionLoading,
  selectArtistProductions,
  selectArtistProductionsLoading,
  selectArtistPerformedDates,
  selectArtistVideoFormatsNames,
  selectArtistImageFormatsNames,
  selectChosenProfileDisplayRepertoire,
};
